<template>
  <v-container fluid class="page page-provider-connection-create">

    <!-- Breadcrumb -->
    <v-row class="mt-4 mb-6">
      <v-col cols="12" class="d-flex align-center justify-start page-breadcrumb-new">
        <!-- (breadcrumb) -->
        <v-icon small class="mr-2">mdi-link</v-icon>
        <router-link :to="{ name: 'provider-connection-list' }">Shipper Accounts</router-link>
        <div class="separator">/</div>
        <div class="active-item">
          <span class="label-prefix">Create {{ $root.providerAliasToName(providerAlias) }} Connection</span>
        </div>
      </v-col>
    </v-row>

    <!-- (connection saving) -->
    <template v-if="isSaving">
      <v-row class="mt-4">
        <v-col align="center">
          <v-progress-circular indeterminate :size="50" color="primary" />
        </v-col>
      </v-row>
    </template>

    <!-- (connection creation error) -->
    <template v-else-if="createConnectionError">
      <v-row class="mt-2">
        <v-col class="d-flex align-center justify-center">
          <div class="info-text">Connection could not be created.</div>
        </v-col>
      </v-row>
      <v-row class="mt-4">
        <v-col align="center">
          <v-row class="mb-5 align-center justify-center error-item">
            <v-col cols="1" align="center">
              <v-icon color="red">mdi-alert-circle-outline</v-icon>
            </v-col>
            <v-col cols="11" align="left">
              <span>{{ createConnectionError }}</span>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row class="mt-0">
        <v-col align="center">
          <v-btn text color="primary" @click="createConnectionError = null">
            Back to form
          </v-btn>
        </v-col>
      </v-row>
    </template>

    <!-- (connection saved message) -->
    <template v-else-if="createdConnection">
      <v-row class="mt-8">
        <v-col class="d-flex align-center justify-center">
          <v-icon color="green" class="pr-2">mdi-check-circle</v-icon>
          <div style="font-size:1.08em">Connection to {{ $root.providerAliasToName(providerAlias) }} created!</div>
        </v-col>
      </v-row>
      <v-row v-if="wasVehicleLotCreated">
        <v-col class="d-flex align-center justify-center">
          <div style="font-size:1.08em">A vehicle lot was also created using this address.</div>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="d-flex align-center justify-center">
          <v-btn large text color="primary" @click="goToProviderConnectionsPage">
            <v-icon class="pr-2" style="font-size:16px" color="primary">mdi-open-in-new</v-icon>
            View connected shipper accounts
          </v-btn>
        </v-col>
      </v-row>
    </template>

    <!-- Create interface -->
    <template v-else>

      <!-- Page Title and Close Action -->
      <v-row class="mt-0">
        <v-col cols="9">
          <v-toolbar>
            <v-toolbar-title class="page-title">Create a {{ $root.providerAliasToName(providerAlias) }} Connection</v-toolbar-title>
          </v-toolbar>
        </v-col>
        <v-col cols="3" align="right" class="mt-6">
          <!-- (close action) -->
          <v-btn text color="primary" class="white--text" @click="cancel">
            Close
          </v-btn>
        </v-col>
      </v-row>

      <!-- Creation Form -->
      <div class="mt-4 page-content">

        <!-- API Key -->
        <v-row no-gutters>
          <v-col>
            Provider API Key:
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col cols="12" xs="12" sm="7" md="6" lg="5">
            <v-text-field
                hide-details="auto"
                :append-icon="showApiKey ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showApiKey ? 'text' : 'password'"
                @click:append="showApiKey = !showApiKey"
                @change="isKeyChanged = true"
                v-model="key" />
          </v-col>
        </v-row>
        <!-- (connect button) -->
        <v-row class="mt-5" v-if="supportsConnect">
          <v-col cols="12" xs="12" sm="7" md="6" lg="5" align="center">
            <v-btn width="150" color="amber darken-3" @click="connectToProvider" :disabled="!key">
              Connect
            </v-btn>
          </v-col>
        </v-row>

        <!-- (connecting) -->
        <template v-if="isAccountDataLoading">
          <v-row class="mt-8">
            <v-col cols="12" xs="12" sm="7" md="6" lg="5" align="center">
              <v-progress-circular indeterminate :size="24" color="white" />
            </v-col>
          </v-row>
        </template>

        <!-- Account Management -->
        <template v-else>
          <!-- (error connecting message) -->
          <v-row class="mt-2" v-if="providerConnectionError">
            <v-col cols="12" xs="12" sm="7" md="6" lg="5" align="center" class="error-message-text">
              {{ providerConnectionError }}
            </v-col>
          </v-row>

          <!-- Selected Account -->
          <template v-if="loadedAccounts">
            <v-row class="mt-8">
              <v-col>
                Select Account:
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="12" xs="12" sm="7" md="6" lg="5">
                <v-select
                    :items="loadedAccounts"
                    item-text="name"
                    item-value="thirdparty_id"
                    v-model="selectedAccountId"
                    @change="loadBrands" />
              </v-col>
            </v-row>
          </template>

          <!-- Selected Brands -->
          <v-row class="mt-6 mb-8" v-if="isBrandDataLoading">
            <v-col cols="12" xs="12" sm="7" md="6" lg="5" align="center">
              <v-progress-circular indeterminate :size="24" color="white" />
            </v-col>
          </v-row>
          <template v-else-if="loadedBrands">
            <v-row class="mt-6 mb-1">
              <v-col>
                Select Brands:
              </v-col>
            </v-row>
            <v-row no-gutters v-for="(brand, index) in loadedBrands" :key="`brand-${index}`">
              <v-col cols="12" xs="12" sm="7" md="6" lg="5" class="ml-6">
                <v-checkbox
                    v-model="selectedBrands"
                    :value="brand.thirdparty_id"
                    :label="brand.name" />
              </v-col>
            </v-row>
          </template>

          <!-- Selected Statuses -->
          <template v-if="selectedAccountId && allowStatusConfiguration">
            <v-row class="mt-6 mb-1">
              <v-col>
                Include Status(es):
              </v-col>
            </v-row>
            <v-row no-gutters v-for="status in availableStatuses" :key="status">
              <v-col cols="12" xs="12" sm="7" md="6" lg="5" class="ml-6">
                <v-checkbox
                    v-model="selectedStatuses"
                    :value="status"
                    :label="status" />
              </v-col>
            </v-row>
          </template>

        </template> <!-- END account management -->

      </div> <!-- END form --->

      <!-- Page Actions (cancel, save) -->
      <v-row class="mt-6 mb-4">
        <v-col cols="6" class="pl-8">
          <v-btn text color="primary" width="100" @click="cancel">Cancel</v-btn>
        </v-col>
        <v-col cols="6" class="pr-8" align="right">
          <v-btn color="primary" v-if="isReadyToSave" @click="create">Create Connection</v-btn>
        </v-col>
      </v-row>

    </template>

  </v-container>

</template>

<script>
export default {
  name: 'ProviderConnectionCreatePage',

  components: {
  },

  data () {
    return {
      allowStatusConfiguration: false,
      connection: null,
      isSaving: false,
      // ----------------- loaded
      key: null,
      isKeyChanged: false,
      showApiKey: false,
      isAccountDataLoading: false,
      isBrandDataLoading: false,
      providerConnectionError: null,
      loadedAccounts: null,
      loadedBrands: null,
      // ------------------ selected
      selectedAccountId: null,
      selectedBrands: [],
      selectedStatuses: [],
      // ------------------ created
      createdConnection: null,
      wasVehicleLotCreated: false,
      createConnectionError: null
    }
  },

  props: {
    providerAlias: {
      type: String,
      required: true
    }
  },

  computed: {
    supportsConnect () {
      return this.providerAlias === 'leaflink'
    },
    availableStatuses () {
      return (this.allowStatusConfiguration)
        ? ['submitted', 'accepted', 'fulfilled']
        : ['accepted', 'fulfilled'] // forced requirement
    },
    isReadyToSave () {
      return (this.allowStatusConfiguration)
        ? this.selectedAccountId && this.selectedStatuses.length > 0 && this.selectedBrands.length > 0
        : this.selectedAccountId && this.selectedBrands.length > 0
    },
    selectedAccount () {
      return (this.loadedAccounts && this.selectedAccountId)
        ? this.loadedAccounts.find(item => item.thirdparty_id === this.selectedAccountId)
        : null
    }
  },

  async beforeMount () {
    // Create draft connection
    const data = {
      provider_alias: this.providerAlias,
      name: this.$root.providerAliasToName(this.providerAlias)
    }
    this.connection = await this.$store.dispatch('sh/createDraft', { resource: 'providerConnection', data })
  },

  methods: {
    cancel () {
      // Return to list page
      this.goToProviderConnectionsPage()
    },

    goToProviderConnectionsPage () {
      this.$root.goTo('provider-connection-list')
    },

    async create () {
      this.isSaving = true

      try {
        // Prepare data
        const name = (this.selectedAccount) ? this.selectedAccount.name : this.connection.name
        const data = {
          api_key: this.key,
          provider_alias: this.providerAlias,
          account_id: this.selectedAccountId,
          name,
          settings: {
            status: (this.allowStatusConfiguration) ? this.selectedStatuses : this.availableStatuses,
            brands: this.selectedBrands
          }
        }

        // Create connection
        const createdConnection = await this.$store.dispatch('sh/dataApi', {
          method: 'post',
          uri: 'provider-connection',
          opts: {
            bodyData: data
          }
        })
        this.createdConnection = createdConnection
        this.wasVehicleLotCreated = createdConnection.vehicle_lot_created

        // Refresh loaded connections in store
        await this.$store.dispatch('sh/dataApi', {
          method: 'load',
          uri: 'provider-connections',
          opts: {
            resource: 'providerConnections',
            params: { with: 'shipper' },
            all: true,
            force: true
          }
        })
      } catch (err) {
        this.createConnectionError = err.message || err.text || err
      } finally {
        this.isSaving = false
      }
    },

    async connectToProvider () {
      this.isKeyChanged = false
      this.loadedBrands = null
      this.providerConnectionError = null
      await this.loadAccounts()
    },

    // Load available accounts for the connection
    async loadAccounts () {
      this.isAccountDataLoading = true

      try {
        const accounts = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: 'provider/accounts',
          headers: { provider_api_key: this.key },
          opts: {
            params: { provider: this.providerAlias }
          }
        })
        if (accounts.items && accounts.items.length > 0) {
          this.loadedAccounts = accounts.items
        }
      } catch (err) {
        const message = (err.status === 403) ? 'Invalid API Key' : err.message
        this.providerConnectionError = message
      } finally {
        this.isAccountDataLoading = false
      }
    },

    // Load brands for the selected account
    async loadBrands () {
      this.isBrandDataLoading = true

      try {
        const brands = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: 'provider/brands',
          headers: { provider_api_key: this.key },
          opts: {
            params: { provider: this.providerAlias, account_id: this.selectedAccountId }
          }
        })
        if (brands.items && brands.items.length > 0) {
          this.loadedBrands = brands.items
        }
      } catch (err) {
        console.log(`Error retrieving brands for provider ${this.providerAlias} =>`, err)
      }

      this.isBrandDataLoading = false
    }
  }
}
</script>

<style scoped>
  /* Info text for processing and results display */
  .page-provider-connection-create .info-text {
    font-size: 1.04rem;
    color: #a2a2a2;
  }

  /* Error display */
  .page-provider-connection-create .error-item {
    color: #F44336;
    font-size: 0.9rem;
    border: 1px solid #F44336;
    border-radius: 4px;
    padding: 3px 3px;
    max-width: 500px;
  }

  .page-provider-connection-create .error-message-text {
    font-size: 15px;
    color: #cc0000;
    font-weight: 400;
  }
</style>
